import { useEffect, useMemo, useState } from "react";
import { ArrowRight, CheckCircle2, Loader2, ShieldCheck, UtensilsCrossed } from "lucide-react";
import type { Event, PotluckConfig } from "@/lib/event-schema";
import { api, type PotluckPublicItem } from "@/lib/api-client";
import { cn } from "@/lib/cn";

type Props = {
  config: PotluckConfig;
  event: Event;
};

export function PotluckModule({ config, event }: Props) {
  const [categoryId, setCategoryId] = useState(config.categories[0]?.id ?? "");
  const [dish, setDish] = useState("");
  const [contributor, setContributor] = useState("");
  const [quantity, setQuantity] = useState("");
  const [notes, setNotes] = useState("");
  const [passcode, setPasscode] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [items, setItems] = useState<PotluckPublicItem[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const showList = config.showSubmissions !== false;

  useEffect(() => {
    if (!showList) return;
    let cancelled = false;
    api.potluck
      .list(event.slug)
      .then((data) => {
        if (!cancelled) setItems(data);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(String(err.message ?? err));
      });
    return () => {
      cancelled = true;
    };
  }, [event.slug, showList]);

  const categoryLabelById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of config.categories) map.set(c.id, c.label);
    return map;
  }, [config.categories]);

  // Group items by category. Match case-insensitively so legacy entries with
  // varied capitalization (e.g. "Main Dish" vs "Main dish") still land in the
  // intended bucket.
  const grouped = useMemo(() => {
    if (!items) return null;
    const norm = (s: string) => s.trim().toLowerCase();
    const labelByNorm = new Map<string, string>();
    const buckets = new Map<string, PotluckPublicItem[]>();
    for (const c of config.categories) {
      labelByNorm.set(norm(c.label), c.label);
      buckets.set(c.label, []);
    }
    const other: PotluckPublicItem[] = [];
    for (const item of items) {
      const target = labelByNorm.get(norm(item.category));
      if (target) {
        buckets.get(target)!.push(item);
      } else {
        other.push(item);
      }
    }
    if (other.length) buckets.set("Other", other);
    return Array.from(buckets.entries()).filter(([, list]) => list.length);
  }, [items, config.categories]);

  function countFor(label: string) {
    if (!items) return null;
    const target = label.trim().toLowerCase();
    return items.filter((i) => i.category.trim().toLowerCase() === target).length;
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setStatus("submitting");
    setErrorMsg("");
    try {
      const category = categoryLabelById.get(categoryId) ?? categoryId;
      const created = await api.potluck.add(event.slug, {
        name: dish,
        contributorName: contributor,
        category,
        quantity,
        notes: notes || undefined,
        passcode: passcode || undefined,
      });
      setItems((prev) => (prev ? [...prev, created] : [created]));
      setStatus("success");
      setDish("");
      setQuantity("");
      setNotes("");
    } catch (err) {
      setStatus("error");
      setErrorMsg(err instanceof Error ? err.message : "Something went wrong.");
    }
  }

  return (
    <section
      id="potluck"
      className="mx-auto w-full max-w-6xl px-5 py-16 sm:px-6 lg:px-8 lg:py-24"
      aria-labelledby={`${event.slug}-potluck`}
    >
      <header className="text-center mb-10">
        <p className="eyebrow text-accent">A community table</p>
        <h2
          id={`${event.slug}-potluck`}
          className="display-title mt-3 text-[clamp(2rem,4.4vw,3.25rem)] leading-tight"
        >
          {config.title}
        </h2>
        {config.description ? (
          <p className="mx-auto mt-3 max-w-2xl text-balance text-muted">{config.description}</p>
        ) : null}
      </header>

      {config.notice ? (
        <div className="mx-auto mb-10 max-w-3xl">
          <div className="flex items-start gap-3 rounded-2xl border border-accent/40 bg-accent-soft/40 px-5 py-4">
            <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-accent" strokeWidth={1.75} />
            <div>
              <p className="text-sm font-medium text-accent-ink dark:text-accent">
                Halal kitchen, please
              </p>
              <p className="mt-0.5 text-sm text-muted leading-relaxed">{config.notice}</p>
            </div>
          </div>
        </div>
      ) : null}

      <div className="grid gap-8 lg:grid-cols-[1.1fr,1.5fr]">
        {/* Categories selector */}
        <div className="surface-card p-6">
          <p className="eyebrow mb-4 flex items-center gap-2">
            <UtensilsCrossed className="h-3.5 w-3.5 text-accent" strokeWidth={1.75} />
            Pick a category
          </p>
          <div className="grid gap-2">
            {config.categories.map((cat) => {
              const active = cat.id === categoryId;
              const count = countFor(cat.label);
              return (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setCategoryId(cat.id)}
                  className={cn(
                    "flex items-center justify-between gap-3 rounded-2xl border px-4 py-3 text-left transition-all focus-ring",
                    active
                      ? "border-accent/60 bg-accent-soft/50 text-accent-ink dark:text-accent"
                      : "border-line/60 hover:border-line bg-surface hover:bg-surface-raised",
                  )}
                >
                  <span className="flex items-center gap-3">
                    {cat.emoji ? (
                      <span aria-hidden className="text-xl">
                        {cat.emoji}
                      </span>
                    ) : null}
                    <span className="text-sm font-medium">{cat.label}</span>
                  </span>
                  <span className="flex items-center gap-3">
                    {count !== null ? (
                      <span className="text-[11px] uppercase tracking-wider text-subtle">
                        {count}
                      </span>
                    ) : null}
                    <span
                      className={cn(
                        "h-2.5 w-2.5 rounded-full transition-transform",
                        active ? "bg-accent scale-110" : "bg-line",
                      )}
                    />
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Form */}
        <form onSubmit={submit} className="surface-card p-6 lg:p-8">
          {status === "success" ? (
            <div className="flex flex-col items-center text-center py-10">
              <div className="grid h-12 w-12 place-items-center rounded-full bg-accent/15 text-accent">
                <CheckCircle2 className="h-5 w-5" strokeWidth={1.75} />
              </div>
              <h3 className="mt-4 font-display text-xl font-light">Added, thank you</h3>
              <p className="mt-1 text-sm text-muted">
                Your dish is on the list below. Keep the passcode if you want to edit later.
              </p>
              <button
                type="button"
                onClick={() => setStatus("idle")}
                className="mt-6 text-sm text-accent hover:underline"
              >
                Add another dish
              </button>
            </div>
          ) : (
            <div className="grid gap-5">
              <Field label="Dish name">
                <input
                  type="text"
                  required
                  placeholder="Saffron rice"
                  value={dish}
                  onChange={(e) => setDish(e.target.value)}
                  className="form-input"
                />
              </Field>

              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Your name">
                  <input
                    type="text"
                    required
                    placeholder="Layla"
                    value={contributor}
                    onChange={(e) => setContributor(e.target.value)}
                    className="form-input"
                  />
                </Field>
                <Field label="Quantity">
                  <input
                    type="text"
                    required
                    placeholder="serves ~10"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    className="form-input"
                  />
                </Field>
              </div>

              <Field label="Notes (optional)" hint="Allergens, serving instructions, anything useful.">
                <textarea
                  rows={2}
                  placeholder="Contains nuts; serve warm."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="form-input"
                />
              </Field>

              <Field label="Passcode" hint="Use this later if you need to remove the dish.">
                <input
                  type="text"
                  required
                  placeholder="e.g. saffron-7"
                  value={passcode}
                  onChange={(e) => setPasscode(e.target.value)}
                  className="form-input"
                />
              </Field>

              {status === "error" ? (
                <p className="text-sm text-red-600 dark:text-red-400">{errorMsg}</p>
              ) : null}

              <div className="flex items-center justify-end pt-1">
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className={cn(
                    "inline-flex items-center gap-2 rounded-full bg-ink px-5 py-3 text-sm font-medium text-canvas",
                    "dark:bg-accent dark:text-accent-on",
                    "shadow-glow transition-all hover:bg-ink/90 dark:hover:bg-accent/90 focus-ring",
                    "disabled:opacity-60",
                  )}
                >
                  {status === "submitting" ? (
                    <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.75} />
                  ) : (
                    <ArrowRight className="h-4 w-4" strokeWidth={1.75} />
                  )}
                  Add my dish
                </button>
              </div>
            </div>
          )}
        </form>
      </div>

      {showList ? (
        <PotluckList
          grouped={grouped}
          loading={items === null && !loadError}
          loadError={loadError}
          total={items?.length ?? 0}
        />
      ) : null}
    </section>
  );
}

function PotluckList({
  grouped,
  loading,
  loadError,
  total,
}: {
  grouped: Array<[string, PotluckPublicItem[]]> | null;
  loading: boolean;
  loadError: string | null;
  total: number;
}) {
  return (
    <div className="mt-16">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
        <div>
          <p className="eyebrow text-accent">On the table so far</p>
          <h3 className="display-title mt-2 text-2xl font-light">
            {total} {total === 1 ? "dish" : "dishes"} signed up
          </h3>
        </div>
        <p className="text-xs text-subtle">
          Updates the moment someone adds one
        </p>
      </div>

      {loading ? (
        <ListSkeleton />
      ) : loadError ? (
        <div className="surface-card p-6 text-sm text-muted">
          Couldn't load the list right now. Refresh in a moment.
        </div>
      ) : !grouped || grouped.length === 0 ? (
        <div className="surface-card p-8 text-center">
          <p className="font-display text-lg font-light">No dishes yet</p>
          <p className="mt-1 text-sm text-muted">
            Be the first to sign up — it's a generous start.
          </p>
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2">
          {grouped.map(([category, list]) => (
            <section key={category} className="surface-card p-5">
              <header className="mb-4 flex items-center justify-between gap-3">
                <h4 className="font-display text-lg font-light text-ink">{category}</h4>
                <span className="rounded-full border border-line/70 bg-surface px-2.5 py-0.5 text-[11px] uppercase tracking-[0.14em] text-subtle">
                  {list.length}
                </span>
              </header>
              <ul className="divide-y divide-line/60">
                {list.map((item) => (
                  <li key={item.id} className="py-3 first:pt-0 last:pb-0">
                    <div className="flex items-baseline justify-between gap-3">
                      <p className="font-medium text-ink leading-snug">{item.name}</p>
                      <span className="shrink-0 text-xs text-subtle font-mono">
                        {item.quantity}
                      </span>
                    </div>
                    <p className="mt-0.5 text-sm text-muted">
                      <span className="text-ink/80">{item.contributorName}</span>
                    </p>
                    {item.notes ? (
                      <p className="mt-1 text-xs text-subtle italic leading-relaxed">
                        {item.notes}
                      </p>
                    ) : null}
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

function ListSkeleton() {
  return (
    <div className="grid gap-6 md:grid-cols-2">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="surface-card p-5 animate-pulse">
          <div className="mb-4 h-5 w-32 rounded bg-line/70" />
          <div className="space-y-3">
            <div className="h-4 w-3/4 rounded bg-line/60" />
            <div className="h-4 w-2/3 rounded bg-line/60" />
            <div className="h-4 w-1/2 rounded bg-line/60" />
          </div>
        </div>
      ))}
    </div>
  );
}

function Field({
  label,
  children,
  hint,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-ink">{label}</span>
      {children}
      {hint ? <span className="mt-1.5 block text-xs text-subtle">{hint}</span> : null}
    </label>
  );
}
